import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Table, Tag, Alert } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { get } from "../../api/axios";
import { checkPermission } from "../auth/ProtectedRoutes";
import {
  ActionCard,
  BarList,
  Donut,
  Panel,
  StatCard,
  formatMoney,
  formatNumber,
} from "./widgets";

/**
 * مصادر بطاقات "يحتاج إجراءً منك".
 *
 * ملاحظة عن الدقة: عندما يرجّع الـ backend حقل meta.total نستخدمه كما هو.
 * أما البنود التي لا يوفّر لها الـ backend فلترة جاهزة (الحوالات والاستردادات)
 * فنحسبها من الصفحة الأولى باستخدام predicate ونعرضها بعلامة "~".
 * حين يضيف فريق الـ backend فلترة بالحالة، احذف الـ predicate وأضف البارامتر
 * إلى الـ endpoint هنا فقط.
 */
const ACTION_SOURCES = [
  {
    key: "properties_pending",
    label: "عقارات غير معروضة",
    endpoint: "properties?status=pending",
    to: "/properties",
    tone: "warning",
    permission: "list-property",
  },
  {
    key: "properties_incomplete",
    label: "عقارات غير مكتملة",
    endpoint: "properties?status=incomplete",
    to: "/properties",
    tone: "info",
    permission: "list-property",
  },
  {
    key: "properties_blocked",
    label: "عقارات موقوفة",
    endpoint: "properties?status=blocked",
    to: "/properties",
    tone: "danger",
    permission: "list-property",
  },
  {
    key: "payouts_pending",
    label: "حوالات قيد التنفيذ",
    endpoint: "payouts",
    to: "/payouts",
    tone: "danger",
    predicate: (row) => row?.payout_status !== "منفذة",
    hint: "محسوب من أحدث دفعة سجلات — يحتاج فلتر بالحالة من الـ backend للعدد الدقيق.",
  },
  {
    key: "refunds_pending",
    label: "استردادات معلّقة",
    endpoint: "refunds",
    to: "/refunds",
    tone: "danger",
    predicate: (row) => row?.status !== "منفذ",
    hint: "محسوب من أحدث دفعة سجلات — يحتاج فلتر بالحالة من الـ backend للعدد الدقيق.",
  },
  {
    key: "unit_registrations",
    label: "تسجيلات وحدات للمتابعة",
    endpoint: "unit-registrations",
    to: "/unit-registrations",
    tone: "info",
  },
  {
    key: "contact_us",
    label: "رسائل تواصل معنا",
    endpoint: "contact-us",
    to: "/contact-us",
    tone: "neutral",
  },
  {
    key: "suggestions",
    label: "اقتراحات وشكاوى",
    endpoint: "suggestions",
    to: "/suggestions",
    tone: "neutral",
  },
];

// يحوّل قيمة قد تأتي كنص ("12.4%") أو رقم إلى رقم
const toNumber = (value) => {
  if (typeof value === "number") return value;
  const parsed = parseFloat(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const extractCount = (res, predicate) => {
  const rows = res?.data?.data;
  const metaTotal = res?.data?.meta?.total;

  if (!predicate && typeof metaTotal === "number") {
    return { count: metaTotal, approximate: false };
  }
  if (Array.isArray(rows)) {
    return {
      count: predicate ? rows.filter(predicate).length : rows.length,
      approximate: true,
    };
  }
  return { count: 0, approximate: false };
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState({});
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState(null);
  const [views, setViews] = useState(null);
  const [rating, setRating] = useState(null);
  const [latestReservations, setLatestReservations] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [failed, setFailed] = useState(false);

  const visibleActions = ACTION_SOURCES.filter(
    (source) => !source.permission || checkPermission(source.permission),
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    setFailed(false);

    // silent: true حتى لا تتراكم عشر رسائل خطأ على الشاشة إن تعطّل الخادم؛
    // نعرض تنبيهاً واحداً بدلاً من ذلك.
    const options = { silent: true };

    const [
      statsRes,
      salesRes,
      viewsRes,
      ratingRes,
      reservationsRes,
      ...actionResults
    ] = await Promise.all([
      get("get-makan-statistics", options),
      get("statistics/sales", options),
      get("statistics/views", options),
      get("statistics/rating", options),
      get("reservations?page=1", options),
      ...visibleActions.map((source) => get(source.endpoint, options)),
    ]);

    setStats(statsRes?.data || null);
    setSales(salesRes?.data || null);
    setViews(viewsRes?.data || null);
    setRating(ratingRes?.data || null);
    setLatestReservations((reservationsRes?.data?.data || []).slice(0, 5));

    const nextActions = {};
    visibleActions.forEach((source, index) => {
      nextActions[source.key] = extractCount(
        actionResults[index],
        source.predicate,
      );
    });
    setActions(nextActions);

    if (!statsRes && !salesRes) setFailed(true);
    setUpdatedAt(new Date());
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const adminName = localStorage.getItem("authNameAdmin") || "";
  const today = new Date().toLocaleDateString("ar-EG", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const totalPendingActions = Object.values(actions).reduce(
    (sum, item) => sum + (item?.count || 0),
    0,
  );

  const reservationColumns = [
    {
      title: "رقم الحجز",
      dataIndex: "id",
      render: (id) => <strong>{id}</strong>,
    },
    {
      title: "الوحدة",
      render: (record) => record?.property?.name || "-",
    },
    {
      title: "الضيف",
      render: (record) => record?.user?.name || "-",
    },
    {
      title: "المبلغ",
      dataIndex: "total_price",
      render: (value) => formatNumber(toNumber(value)),
    },
    {
      title: "الحالة",
      dataIndex: "status",
      render: (status) => {
        const colors = {
          مؤكد: "green",
          ملغي: "red",
          منتهي: "default",
        };
        return <Tag color={colors[status] || "orange"}>{status || "-"}</Tag>;
      },
    },
    {
      title: "التاريخ",
      dataIndex: "date_of_creation",
    },
  ];

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <div>
          <h4 className="dashboard__greeting">
            أهلاً {adminName || "بك"} 👋
          </h4>
          <p className="dashboard__date">{today}</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {updatedAt && (
            <span className="text-muted" style={{ fontSize: 12 }}>
              آخر تحديث: {updatedAt.toLocaleTimeString("ar-EG")}
            </span>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={loadAll}
            loading={loading}
            size="small"
          >
            تحديث
          </Button>
        </div>
      </div>

      {failed && (
        <Alert
          className="mb-3"
          type="error"
          showIcon
          message="تعذّر تحميل بيانات اللوحة"
          description="تحقق من الاتصال بالخادم ثم اضغط تحديث. التفاصيل في الـ Console."
        />
      )}

      <div className="dashboard__section-title">
        يحتاج إجراءً منك
        {!loading && (
          <span className="text-muted fw-normal" style={{ fontSize: 12 }}>
            {totalPendingActions > 0
              ? `${formatNumber(totalPendingActions)} بند`
              : "لا شيء معلّق"}
          </span>
        )}
      </div>

      <div className="dashboard-grid dashboard-grid--actions">
        {visibleActions.map((source) => (
          <ActionCard
            key={source.key}
            label={source.label}
            to={source.to}
            tone={source.tone}
            hint={source.hint}
            loading={loading}
            count={actions[source.key]?.count || 0}
            approximate={actions[source.key]?.approximate}
          />
        ))}
      </div>

      <div className="dashboard__section-title">مؤشرات عامة</div>

      <div className="dashboard-grid dashboard-grid--stats">
        <StatCard
          label="إجمالي الحجوزات"
          value={formatNumber(stats?.reservations?.total)}
          sub={`مؤكدة ${formatNumber(
            stats?.reservations?.status?.confirmed,
          )} · ملغية ${formatNumber(stats?.reservations?.status?.canceled)}`}
          loading={loading}
        />
        <StatCard
          label="المدفوع من الضيوف (صافي)"
          value={formatMoney(sales?.paid?.total_price)}
          sub="بعد استبعاد المسترد"
          loading={loading}
        />
        <StatCard
          label="صافي دخل مكان"
          value={formatMoney(
            sales?.paid?.commission_amount_and_service_amount,
          )}
          sub="الرسوم + العمولة بدون ضريبة"
          loading={loading}
        />
        <StatCard
          label="مستحقات المضيفين"
          value={formatMoney(sales?.paid?.owner_total_payout)}
          sub="صافي مبيعات المضيفين"
          loading={loading}
        />
        <StatCard
          label="عدد المستخدمين"
          value={formatNumber(stats?.users?.all?.all)}
          sub={`ضيوف ${formatNumber(
            stats?.users?.guests?.all,
          )} · مضيفين ${formatNumber(stats?.users?.owners?.all)}`}
          loading={loading}
        />
        <StatCard
          label="التقييم العام"
          value={formatNumber(toNumber(rating?.rate_total_rating), {
            decimals: 1,
          })}
          sub={`${formatNumber(rating?.rate_count)} تقييم`}
          loading={loading}
        />
      </div>

      <div className="dashboard__section-title">تفصيل</div>

      <div className="dashboard-grid dashboard-grid--panels">
        <Panel
          title="الحجوزات حسب المنصة"
          extra={
            <Link to="/reservations" className="small text-decoration-none">
              الكل ←
            </Link>
          }
        >
          <BarList
            loading={loading}
            items={[
              { label: "الويب", value: stats?.reservations?.platforms?.web },
              { label: "iOS", value: stats?.reservations?.platforms?.ios },
              {
                label: "Android",
                value: stats?.reservations?.platforms?.android,
              },
              {
                label: "من المضيف",
                value: stats?.reservations?.source?.owner,
                color: "#7f8c8d",
              },
            ]}
          />
        </Panel>

        <Panel
          title="العقارات حسب الحالة"
          extra={
            <Link to="/properties" className="small text-decoration-none">
              الكل ←
            </Link>
          }
        >
          <BarList
            loading={loading}
            items={(stats?.properties || []).map((item) => ({
              label: item.name,
              value: item.count,
            }))}
          />
        </Panel>

        <Panel title="مؤشرات التحويل">
          <div className="d-flex flex-wrap justify-content-around gap-3">
            <Donut
              value={toNumber(views?.view_to_click_rate)}
              label="مشاهدة ← نقرة"
              sublabel={`${formatNumber(views?.total_views)} مشاهدة`}
            />
            <Donut
              value={toNumber(views?.click_to_reservation_rate)}
              label="نقرة ← حجز"
              sublabel={`${formatNumber(views?.total_clicks)} نقرة`}
              color="#2471a3"
            />
          </div>
        </Panel>

        <Panel
          title="المستخدمون حسب المنصة"
          extra={
            <Link to="/guests" className="small text-decoration-none">
              الضيوف ←
            </Link>
          }
        >
          <BarList
            loading={loading}
            items={[
              { label: "الويب", value: stats?.users?.all?.web },
              { label: "iOS", value: stats?.users?.all?.ios },
              { label: "Android", value: stats?.users?.all?.android },
            ]}
          />
        </Panel>
      </div>

      <div className="dashboard__section-title">
        أحدث الحجوزات
        <Link to="/reservations" className="small text-decoration-none">
          عرض الكل ←
        </Link>
      </div>

      <div className="dashboard-panel">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          pagination={false}
          locale={{ emptyText: "لا يوجد حجوزات" }}
          columns={reservationColumns}
          dataSource={latestReservations}
          scroll={{ x: 720 }}
        />
      </div>
    </div>
  );
};

export default Dashboard;
